import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiPlus, FiEdit, FiTrash2, FiUpload, FiImage, FiX, FiSearch } from 'react-icons/fi';
import Cropper from 'react-easy-crop';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';

const BannersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.variant === 'primary' ? '#5efba9' : 'white'};
  color: ${props => props.variant === 'primary' ? '#1f2937' : '#6b7280'};
  border: ${props => props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#4de89e' : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const CropModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const CropContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CropArea = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
`;

const CropControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ZoomLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  min-width: 60px;
`;

const ZoomSlider = styled.input`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #5efba9;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #5efba9;
    cursor: pointer;
    border: none;
  }
`;

const CropButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const BannerSizeInfo = styled.div`
  background: #f0fdf4;
  border: 1px solid #5efba9;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #166534;
  
  strong {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;

  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
  }

  input {
    display: none;
  }
`;

// Table Styles
const TableWrapper = styled.div`
  ${scrollableTableWrapStyles}
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
`;

const BannerImage = styled.img`
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
`;

const ImagePlaceholder = styled.div`
  width: 100px;
  height: 60px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.active ? '#166534' : '#92400e'};
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: ${props => props.active ? '#5efba9' : 'white'};
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
  border-radius: 8px 8px 0 0;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 2px solid ${props => props.active ? '#5efba9' : 'transparent'};

  &:hover {
    background: ${props => props.active ? '#4de89e' : '#f9fafb'};
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

export default function SuperAdminBanners() {
  const { isArabic } = useLanguage();
  const [banners, setBanners] = useState([]);
  const [bucketFiles, setBucketFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('banners'); // 'banners' or 'bucket'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    img: '',
    duration: '',
    url: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.createRef();
  
  // Image cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(20);
  
  // Recommended banner size: 1200x400px (3:1 aspect ratio)
  const BANNER_ASPECT = 3 / 1; // width / height
  const BANNER_WIDTH = 1200;
  const BANNER_HEIGHT = 400;

  // Fetch banners from table
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBanners(data || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
      alert('Failed to fetch banners: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch files from banner bucket
  const fetchBucketFiles = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('banner')
        .list('', {
          limit: 100,
          offset: 0,
        });

      if (error) throw error;

      // Get public URLs for all files
      const filesWithUrls = (data || []).map((file) => {
        const { data: { publicUrl } } = supabase.storage
          .from('banner')
          .getPublicUrl(file.name);
        return {
          ...file,
          publicUrl
        };
      });

      setBucketFiles(filesWithUrls);
    } catch (err) {
      console.error('Error fetching bucket files:', err);
      alert('Failed to fetch bucket files: ' + err.message);
    }
  };

  useEffect(() => {
    fetchBanners();
    fetchBucketFiles();
  }, []);

  // Create image from file
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  // Get cropped image blob
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = BANNER_WIDTH;
    canvas.height = BANNER_HEIGHT;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      BANNER_WIDTH,
      BANNER_HEIGHT
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setSelectedFile(file);
    
    // Create image preview for cropping
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
    });
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setUploadingFile(true);
    setShowCropModal(false);

    try {
      // Get cropped image blob
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

      // Generate unique filename
      const fileName = `banner_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;

      console.log('Uploading cropped banner to bucket:', fileName);

      // Upload cropped image to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('banner')
        .upload(fileName, croppedImage, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg'
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload image: ' + uploadError.message);
        setSelectedFile(null);
        setImageSrc(null);
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('banner')
        .getPublicUrl(fileName);

      console.log('Upload successful, public URL:', publicUrl);

      // Automatically set the img field with the uploaded URL
      setFormData(prev => ({
        ...prev,
        img: publicUrl,
        name: prev.name || selectedFile.name.split('.')[0] // Set name if not already set
      }));

      // Reset states
      setImageSrc(null);
      setSelectedFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      alert('Banner cropped and uploaded successfully!');
    } catch (err) {
      console.error('Error processing image:', err);
      alert('Failed to process image: ' + err.message);
      setSelectedFile(null);
      setImageSrc(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setSelectedFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      name: banner.name || '',
      img: banner.img || '',
      duration: banner.duration || '',
      url: banner.url || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('banners')
        .delete()
        .eq('id', bannerId);

      if (error) throw error;

      alert('Banner deleted successfully!');
      fetchBanners();
    } catch (err) {
      console.error('Error deleting banner:', err);
      alert('Failed to delete banner: ' + err.message);
    }
  };

  const handleDeleteBucketFile = async (fileName) => {
    if (!window.confirm('Are you sure you want to delete this file from the bucket?')) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from('banner')
        .remove([fileName]);

      if (error) throw error;

      alert('File deleted successfully!');
      fetchBucketFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const bannerData = {
        name: formData.name || null,
        img: formData.img || null,
        duration: formData.duration || null,
        url: formData.url?.trim() || null
      };

      let error;
      if (editingBanner) {
        const { error: updateError } = await supabase
          .from('banners')
          .update(bannerData)
          .eq('id', editingBanner.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('banners')
          .insert([bannerData]);
        error = insertError;
      }

      if (error) throw error;

      alert(editingBanner ? 'Banner updated successfully!' : 'Banner added successfully!');
      setIsModalOpen(false);
      setEditingBanner(null);
      setFormData({
        name: '',
        img: '',
        duration: ''
      });
      fetchBanners();
    } catch (err) {
      console.error('Error saving banner:', err);
      alert('Failed to save banner: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
    setFormData({
      name: '',
      img: '',
      duration: '',
      url: ''
    });
  };

  // Filter banners by search term
  const filteredBanners = useMemo(() => {
    if (!searchTerm.trim()) {
      return banners;
    }
    const term = searchTerm.toLowerCase();
    return banners.filter(banner => {
      const name = (banner.name || '').toLowerCase();
      const duration = (banner.duration || '').toString();
      const url = (banner.url || '').toLowerCase();
      return name.includes(term) || duration.includes(term) || url.includes(term);
    });
  }, [banners, searchTerm]);

  // Filter bucket files by search term
  const filteredBucketFiles = useMemo(() => {
    if (!searchTerm.trim()) {
      return bucketFiles;
    }
    const term = searchTerm.toLowerCase();
    return bucketFiles.filter(file => {
      const fileName = (file.name || '').toLowerCase();
      return fileName.includes(term);
    });
  }, [bucketFiles, searchTerm]);

  // Reset displayed count when tab or search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [activeTab, searchTerm]);

  // Slice to show only displayedCount items based on active tab
  const displayedBanners = filteredBanners.slice(0, displayedCount);
  const hasMoreBanners = filteredBanners.length > displayedCount;
  
  const displayedBucketFiles = filteredBucketFiles.slice(0, displayedCount);
  const hasMoreBucketFiles = filteredBucketFiles.length > displayedCount;

  return (
    <>
      <BannersContainer>
        <Header>
          <Title>{isArabic ? 'إدارة البنرات' : 'Banner Management'}</Title>
          <ActionButtons>
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              <FiPlus /> {isArabic ? 'إضافة بانر' : 'Add Banner'}
            </Button>
          </ActionButtons>
        </Header>

        <TabContainer>
          <Tab active={activeTab === 'banners'} onClick={() => setActiveTab('banners')}>
            {isArabic ? `جدول البنرات (${banners.length})` : `Banners Table (${banners.length})`}
          </Tab>
          <Tab active={activeTab === 'bucket'} onClick={() => setActiveTab('bucket')}>
            {isArabic ? `ملفات البكت (${bucketFiles.length})` : `Bucket Files (${bucketFiles.length})`}
          </Tab>
        </TabContainer>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder={activeTab === 'banners'
              ? (isArabic ? 'ابحث بالاسم أو المدة أو الرابط...' : 'Search by name, duration, or URL...')
              : (isArabic ? 'ابحث باسم الملف...' : 'Search by file name...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title={isArabic ? 'مسح البحث' : 'Clear search'}>
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        {loading ? (
          <p>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</p>
        ) : activeTab === 'banners' ? (
          <>
            {banners.length === 0 ? (
              <TableWrapper>
                <EmptyState>
                  <EmptyTitle>{isArabic ? 'لا توجد بنرات بعد' : 'No Banners Yet'}</EmptyTitle>
                  <EmptyText>{isArabic ? 'ابدأ بإضافة أول بانر' : 'Get started by adding your first banner'}</EmptyText>
                </EmptyState>
              </TableWrapper>
            ) : (
              <TableWrapper>
                <Table>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>{isArabic ? 'الصورة' : 'Image'}</TableHeaderCell>
                      <TableHeaderCell>{isArabic ? 'الاسم' : 'Name'}</TableHeaderCell>
                      <TableHeaderCell>{isArabic ? 'المدة' : 'Duration'}</TableHeaderCell>
                      <TableHeaderCell>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</TableHeaderCell>
                      <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBanners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                          <EmptyTitle>{searchTerm
                            ? (isArabic ? 'لا توجد بنرات مطابقة للبحث' : 'No banners found matching your search')
                            : (isArabic ? 'لا توجد بنرات متاحة' : 'No banners available')}</EmptyTitle>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedBanners.map((banner) => (
                        <TableRow key={banner.id}>
                          <TableCell>
                            {banner.img ? (
                              <BannerImage src={banner.img} alt={banner.name || (isArabic ? 'بانر' : 'Banner')} />
                            ) : (
                              <ImagePlaceholder>{isArabic ? 'لا صورة' : 'No Image'}</ImagePlaceholder>
                            )}
                          </TableCell>
                          <TableCell>
                            <strong>{banner.name || (isArabic ? 'بدون عنوان' : 'Untitled')}</strong>
                          </TableCell>
                          <TableCell>{banner.duration ? `${banner.duration}s` : (isArabic ? 'غير متوفر' : 'N/A')}</TableCell>
                          <TableCell>
                            {banner.created_at
                              ? new Date(banner.created_at).toLocaleDateString()
                              : (isArabic ? 'غير متوفر' : 'N/A')}
                          </TableCell>
                          <TableCell>
                            <ActionButtonsCell>
                              <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEdit(banner)}>
                                <FiEdit />
                              </Button>
                              <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleDelete(banner.id)}>
                                <FiTrash2 />
                              </Button>
                            </ActionButtonsCell>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableWrapper>
            )}
            {!loading && activeTab === 'banners' && banners.length > 0 && hasMoreBanners && (
              <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
                {isArabic ? `تحميل المزيد (${filteredBanners.length - displayedCount} متبقي)` : `Load More (${filteredBanners.length - displayedCount} remaining)`}
              </LoadMoreButton>
            )}
          </>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>{isArabic ? 'المعاينة' : 'Preview'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'اسم الملف' : 'File Name'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الحجم' : 'Size'}</TableHeaderCell>
                  <TableHeaderCell>URL</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {bucketFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                      <EmptyTitle>{isArabic ? 'لا توجد ملفات في البكت' : 'No Files in Bucket'}</EmptyTitle>
                      <EmptyText>{isArabic ? 'قم برفع ملفات إلى بكت البنرات' : 'Upload files to the banner bucket'}</EmptyText>
                    </TableCell>
                  </TableRow>
                ) : filteredBucketFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                      <EmptyTitle>{searchTerm
                        ? (isArabic ? 'لا توجد ملفات مطابقة للبحث' : 'No files found matching your search')
                        : (isArabic ? 'لا توجد ملفات متاحة' : 'No files available')}</EmptyTitle>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedBucketFiles.map((file) => (
                    <TableRow key={file.name}>
                      <TableCell>
                        {file.publicUrl ? (
                          <BannerImage src={file.publicUrl} alt={file.name} />
                        ) : (
                          <ImagePlaceholder>{isArabic ? 'لا توجد معاينة' : 'No Preview'}</ImagePlaceholder>
                        )}
                      </TableCell>
                      <TableCell>{file.name}</TableCell>
                      <TableCell>
                        {file.metadata?.size ? `${(file.metadata.size / 1024).toFixed(2)} KB` : (isArabic ? 'غير متوفر' : 'N/A')}
                      </TableCell>
                      <TableCell>
                        <a href={file.publicUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#5efba9', textDecoration: 'none' }}>
                          {isArabic ? 'عرض الرابط' : 'View URL'}
                        </a>
                      </TableCell>
                      <TableCell>
                        <ActionButtonsCell>
                          <Button
                            variant="secondary"
                            style={{ padding: '6px 12px' }}
                            onClick={() => {
                              setFormData({
                                name: file.name.split('.')[0],
                                img: file.publicUrl,
                                duration: ''
                              });
                              setIsModalOpen(true);
                            }}
                          >
                            <FiPlus /> {isArabic ? 'إنشاء بانر' : 'Create Banner'}
                          </Button>
                          <Button
                            variant="secondary"
                            style={{ padding: '6px 12px' }}
                            onClick={() => handleDeleteBucketFile(file.name)}
                          >
                            <FiTrash2 />
                          </Button>
                        </ActionButtonsCell>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              </Table>
          </TableWrapper>
        )}
        {!loading && activeTab === 'bucket' && hasMoreBucketFiles && (
          <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
            {isArabic ? `تحميل المزيد (${filteredBucketFiles.length - displayedCount} متبقي)` : `Load More (${filteredBucketFiles.length - displayedCount} remaining)`}
          </LoadMoreButton>
        )}
      </BannersContainer>

      {/* Add/Edit Banner Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingBanner ? (isArabic ? 'تعديل البانر' : 'Edit Banner') : (isArabic ? 'إضافة بانر جديد' : 'Add New Banner')}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>{isArabic ? 'الاسم' : 'Name'}</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={isArabic ? 'أدخل اسم البانر' : 'Enter banner name'}
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'رفع صورة' : 'Upload Image'}</Label>
                <BannerSizeInfo>
                  <strong>{isArabic ? '📐 مقاس البانر الموصى به' : '📐 Recommended Banner Size'}</strong>
                  <div>Width: {BANNER_WIDTH}px × Height: {BANNER_HEIGHT}px (3:1 aspect ratio)</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', opacity: 0.8 }}>
                    {isArabic ? 'سيتم قص الصورة تلقائيًا لهذا المقاس' : 'The image will be automatically cropped to this size'}
                  </div>
                </BannerSizeInfo>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploadingFile || showCropModal}
                  style={{ display: 'none' }}
                />
                <UploadArea onClick={() => !uploadingFile && !showCropModal && fileInputRef.current?.click()}>
                  {uploadingFile ? (
                    <div>
                      <p style={{ color: '#5efba9', margin: 0 }}>{isArabic ? 'جارٍ الرفع...' : 'Uploading...'}</p>
                    </div>
                  ) : (
                    <>
                      <FiImage style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                      <p style={{ color: '#6b7280', margin: 0 }}>
                        {isArabic ? 'اضغط لاختيار صورة' : 'Click to select an image file'}
                      </p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                        {isArabic ? 'الامتدادات: JPG, PNG, GIF, WebP' : 'Supports: JPG, PNG, GIF, WebP'}
                      </p>
                    </>
                  )}
                </UploadArea>
                {formData.img && (
                    <ImagePreview src={formData.img} alt={isArabic ? 'معاينة البانر' : 'Banner preview'} />
                )}
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'المدة (ثواني)' : 'Duration (seconds)'}</Label>
                <Input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder={isArabic ? 'أدخل المدة بالثواني' : 'Enter duration in seconds'}
                  min="1"
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {isArabic ? 'عدد الثواني التي سيظهر فيها البانر' : 'The number of seconds the banner will be displayed'}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'الرابط (اختياري)' : 'URL (Optional)'}</Label>
                <Input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder={isArabic ? '/categories/electronics أو https://example.com' : '/categories/electronics or https://example.com'}
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {isArabic ? 'رابط داخلي أو رابط خارجي كامل' : 'Internal route (e.g., /categories/electronics) or external URL (e.g., https://example.com)'}
                </small>
              </FormGroup>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <Button type="button" variant="secondary" onClick={handleCloseModal}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button type="submit" variant="primary">
                  {editingBanner ? (isArabic ? 'تحديث البانر' : 'Update Banner') : (isArabic ? 'إضافة بانر' : 'Add Banner')}
                </Button>
              </div>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Crop Modal */}
      {showCropModal && imageSrc && (
        <CropModal>
          <CropContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>{isArabic ? 'قص صورة البانر' : 'Crop Banner Image'}</h3>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelCrop}
                style={{ padding: '8px 16px' }}
              >
                <FiX /> {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
            
            <BannerSizeInfo>
              <strong>{isArabic ? `📐 المقاس المستهدف: ${BANNER_WIDTH}px × ${BANNER_HEIGHT}px` : `📐 Target Size: ${BANNER_WIDTH}px × ${BANNER_HEIGHT}px`}</strong>
              <div>{isArabic ? 'يرجى ضبط منطقة القص بنسبة 3:1' : 'Please adjust the crop area to match the 3:1 aspect ratio'}</div>
            </BannerSizeInfo>

            <CropArea>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={BANNER_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={true}
              />
            </CropArea>

            <CropControls>
              <ZoomControl>
                <ZoomLabel>{isArabic ? 'التكبير:' : 'Zoom:'}</ZoomLabel>
                <ZoomSlider
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                />
                <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '40px', textAlign: 'right' }}>
                  {zoom.toFixed(1)}x
                </span>
              </ZoomControl>
            </CropControls>

            <CropButtons>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelCrop}
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleCropComplete}
                disabled={uploadingFile}
              >
                {uploadingFile ? (isArabic ? 'جارٍ المعالجة...' : 'Processing...') : (isArabic ? 'قص ورفع' : 'Crop & Upload')}
              </Button>
            </CropButtons>
          </CropContainer>
        </CropModal>
      )}

    </>
  );
}

